import { Directive, OnDestroy, inject } from '@angular/core';
import { MatterportComponent } from '@simlab/matterport';
import { TagComponent } from '@simlab/simlab-facility-management/sub-features/tag';
import { TransformConverter } from '@simlab/transform';
import { IScanControllerToken } from '@simOn/common/scan';
import { Observable, Subject, defer, distinctUntilChanged, firstValueFrom, from, takeUntil, tap } from 'rxjs';

@Directive()
export abstract class ComponentControllerBase implements OnDestroy {
  readonly scanManagerService = inject(IScanControllerToken);
  selectedModel!: Observable<string | undefined>;
  readonly _modeChange: Subject<string> = new Subject<string>();
  readonly _destroy: Subject<void> = new Subject<void>();
  abstract _nodes: Record<string, MatterportComponent | TagComponent>;

  private readonly _markerOps = new Map<string, Promise<unknown>>();

  set mode(context: string | string[]) {
    if (typeof context !== 'string') return;
    this._modeChange.next(context);
  }
  ngOnDestroy(): void {
    this.deleteAllMarkers();
    this._nodes = {};
    this._markerOps.clear();
    this._destroy.next();
    this._destroy.complete();
  }

  /**
   * Serializes marker work per component id. The scan SDK registers a component
   * asynchronously while its duplicate-id check runs synchronously, so two
   * concurrent creations for the same id (store stream vs marker editor) both
   * pass the check and orphan one node on the scene. Running delete-then-add
   * inside this lock keeps a single node per id.
   */
  protected runExclusiveMarkerOp$<T>(id: string, work: () => Observable<T>): Observable<T> {
    return defer(() => {
      const previous = this._markerOps.get(id) ?? Promise.resolve();
      const current = previous.catch(() => undefined).then(() => firstValueFrom(work()));
      this._markerOps.set(
        id,
        current.catch(() => undefined)
      );
      return from(current);
    });
  }

  deleteAllMarkers() {
    Object.keys(this._nodes).map((key) => {
      try {
        this.scanManagerService.component.deleteNote(key);
      } catch {
        console.warn('Missing scan service component');
      }
      delete this._nodes[key];
    });
  }

  protected _deleteMarkerFromScene(id: string) {
    if (this._nodes[id]) {
      this.scanManagerService.component.deleteNote(id);
      delete this._nodes[id];
    }
  }

  get scanTranslator(): TransformConverter {
    return this.scanManagerService.transformConverter;
  }
  protected _modeChangeObserver(...visibleState: string[]) {
    this._modeChange
      .asObservable()
      .pipe(
        distinctUntilChanged(),
        tap((state: string) => (visibleState.includes(state) ? this._showAll() : this._hideAll())),
        takeUntil(this._destroy)
      )
      .subscribe();
  }

  protected _hideAll(): void {
    Object.keys(this._nodes).forEach((compId: string) => {
      const node = this._nodes[compId];
      if ('comp' in node) {
        node.comp.hide();
      } else {
        node.hide();
        node.collision = false;
      }
    });
  }

  protected _showAll(): void {
    Object.keys(this._nodes).forEach((compId: string) => {
      const node = this._nodes[compId];
      if ('comp' in node) node.comp.show();
      else {
        node.show();
        node.collision = true;
      }
    });
    // Object.keys(this._nodes).forEach((compId: string) => {
    //   const comp = this._nodes[compId];
    //   if ('children' in comp) {
    //     comp.comp.show();
    //   } else if ('objects' in comp.inputs) {
    //     comp.show();
    //   }
    // });
  }
}
